import {
  CREATE_PROGRAMME_QUERY,
  PAUSE_PROGRAMME_QUERY,
  RESUME_PROGRAMME_QUERY,
  RUN_PROGRAMME_QUERY_NOW,
} from "@lib/graphql/outreach";
import { AlertTriangle, Pause, Play, Zap } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";
import type { ProgrammeQuerySummary } from "./types";

interface Props {
  programmeId: string;
  queries: ProgrammeQuerySummary[];
  onChanged?: () => void;
}

export function ProgrammeQueriesPanel({ programmeId, queries, onChanged }: Props) {
  const [, createQuery] = useMutation(CREATE_PROGRAMME_QUERY);
  const [, pauseQuery] = useMutation(PAUSE_PROGRAMME_QUERY);
  const [, resumeQuery] = useMutation(RESUME_PROGRAMME_QUERY);
  const [, runNow] = useMutation(RUN_PROGRAMME_QUERY_NOW);

  const [name, setName] = useState("");
  const [searchUrl, setSearchUrl] = useState("");
  const [cron, setCron] = useState("0 7 * * *");
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !searchUrl.trim()) return;
    setBusy(true);
    setErrors([]);
    const result = await createQuery({
      input: {
        outreachProgrammeId: programmeId,
        name: name.trim(),
        searchUrl: searchUrl.trim(),
        scheduleCron: cron,
      },
    });
    const payload = result.data?.createProgrammeQuery;
    if (payload && payload.errors.length === 0) {
      setName("");
      setSearchUrl("");
      onChanged?.();
    } else {
      setErrors(payload?.errors ?? ["Unknown error"]);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-text-primary">Standing LinkedIn jobs searches</h3>

      {queries.length === 0 ? (
        <p className="text-xs text-text-muted">
          No standing queries yet. Save a LinkedIn jobs-search URL below.
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
          {queries.map((q) => (
            <QueryRow
              key={q.id}
              query={q}
              onPause={async () => {
                await pauseQuery({ input: { id: q.id } });
                onChanged?.();
              }}
              onResume={async () => {
                await resumeQuery({ input: { id: q.id } });
                onChanged?.();
              }}
              onRunNow={async () => {
                await runNow({ input: { id: q.id } });
                onChanged?.();
              }}
            />
          ))}
        </ul>
      )}

      <form onSubmit={handleCreate} className="space-y-2 rounded-md border border-border p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Query name (e.g. UK, last 24h)"
            className="rounded-md border border-input-border bg-card px-3 py-2 text-sm"
          />
          <input
            value={searchUrl}
            onChange={(e) => setSearchUrl(e.target.value)}
            placeholder="LinkedIn jobs-search URL"
            className="rounded-md border border-input-border bg-card px-3 py-2 text-sm font-mono"
          />
          <input
            value={cron}
            onChange={(e) => setCron(e.target.value)}
            placeholder="Cron schedule"
            className="rounded-md border border-input-border bg-card px-3 py-2 text-sm font-mono"
          />
        </div>
        {errors.length > 0 && (
          <ul className="text-xs text-red-600 dark:text-red-400">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        <button
          type="submit"
          disabled={busy || !name.trim() || !searchUrl.trim()}
          className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          Save search
        </button>
      </form>
    </div>
  );
}

function QueryRow({
  query,
  onPause,
  onResume,
  onRunNow,
}: {
  query: ProgrammeQuerySummary;
  onPause: () => Promise<void>;
  onResume: () => Promise<void>;
  onRunNow: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    await fn();
    setBusy(false);
  };

  const stats = query.lastRunStats as { new?: number; skipped?: number };

  return (
    <li className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text-primary">{query.name}</span>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              query.status === "active"
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : query.status === "paused"
                  ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {query.status}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-text-muted" title={query.searchUrl}>
          {query.searchUrl}
        </p>
        <p className="mt-0.5 text-xs text-text-muted">
          {query.scheduleCron}
          {query.lastRunAt ? ` · last run ${new Date(query.lastRunAt).toLocaleString()}` : ""}
          {stats?.new != null ? ` · ${stats.new} new, ${stats.skipped ?? 0} skipped` : ""}
        </p>
        {query.lastError && (
          <p className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
            <AlertTriangle size={12} /> {query.lastError}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          onClick={() => run(onRunNow)}
          disabled={busy}
          className="rounded p-1.5 text-text-muted hover:bg-card-hover disabled:opacity-50"
          title="Run now"
        >
          <Zap size={14} />
        </button>
        {query.status === "active" ? (
          <button
            onClick={() => run(onPause)}
            disabled={busy}
            className="rounded p-1.5 text-text-muted hover:bg-card-hover disabled:opacity-50"
            title="Pause"
          >
            <Pause size={14} />
          </button>
        ) : (
          <button
            onClick={() => run(onResume)}
            disabled={busy}
            className="rounded p-1.5 text-text-muted hover:bg-card-hover disabled:opacity-50"
            title="Resume"
          >
            <Play size={14} />
          </button>
        )}
      </div>
    </li>
  );
}
